import { DeleteBtn, PlayPauseBtn } from "../Utility/Utility";
import { useRef, useCallback, useState } from "react";

// ─────────────────────────────────────────────
// LocalVideoBlock
//
// For user-uploaded videos (blob URL or local
// object URL). Uses HTML5 <video> instead of
// an iframe — so we get direct access to the
// media element API (play/pause/ended/etc).
//
// item.res = { url: string, local: true }
//   url can be:
//     • a blob URL   : URL.createObjectURL(file)
//     • a data URL   : "data:video/mp4;base64,..."
//     • a static path: "/uploads/myvideo.mp4"
//
// Aspect ratio: 16:9 (same as YouTube VideoBlock)
// ─────────────────────────────────────────────
export function LocalVideoBlock({ item, onDelete, width, deletable = true }) {
  const height = Math.round(width * (9 / 16));
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  // ── Play / Pause ──────────────────────────────
  // Unlike the iframe strategy, we call the native
  // HTMLVideoElement API directly — no postMessage
  // needed, and we get reliable state from the
  // element's own .paused property.
  const handlePlayPause = useCallback((e) => {
    e.stopPropagation();
    const vid = videoRef.current;
    if (!vid) return;

    if (vid.paused) {
      vid.play().catch(() => {}); // swallow autoplay-policy errors
      setIsPlaying(true);
    } else {
      vid.pause();
      setIsPlaying(false);
    }
  }, []);

  // Overlay click = same toggle
  const handleOverlayClick = useCallback((e) => {
    e.stopPropagation();
    handlePlayPause(e);
  }, [handlePlayPause]);

  // Sync our state when video ends naturally
  const handleEnded = useCallback(() => {
    setIsPlaying(false);
  }, []);

  // Also sync if something external pauses it
  const handlePause = useCallback(() => setIsPlaying(false), []);
  const handlePlay  = useCallback(() => setIsPlaying(true),  []);

  return (
    <div
      style={{
        position: "relative",
        width,
        height,
        borderRadius: 10,
        overflow: "hidden",
        background: "#0d0d0d",
        boxShadow: "0 2px 12px rgba(0,0,0,0.18)",
        flexShrink: 0,
      }}
    >
      {/* Native video element — controls off, pointer events off */}
      <video
        ref={videoRef}
        src={item.res.url}
        width={width}
        height={height}
        onEnded={handleEnded}
        onPause={handlePause}
        onPlay={handlePlay}
        playsInline
        preload="metadata"   // load enough to show first frame
        style={{
          display: "block",
          width: "100%",
          height: "100%",
          objectFit: "cover", // fill the 16:9 box, crop if portrait
          pointerEvents: "none",
          border: "none",
        }}
      />

      {/* Overlay: eats all pointer/hover events */}
      <div
        onClick={handleOverlayClick}
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 5,
          cursor: "pointer",
          background: "transparent",
        }}
      />

      {/* Custom controls */}
      <PlayPauseBtn isPlaying={isPlaying} onClick={handlePlayPause} />
      {deletable && <DeleteBtn onDelete={onDelete} />}
    </div>
  );
}